import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Switch, Select, Button, Row, Col, Alert, Radio, Divider, Badge, Tooltip, Space, Spin } from 'antd';
import { InfoCircleOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPut } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';

const { Option } = Select;

const CATALOG = {
  killSwitch: {
    title: "Global Kill Switch",
    description: "Emergency hard stop for all transactions.",
    keys: [
      { key: "kill_switch.outbound_disabled", type: "BOOLEAN", label: "Disable Outbound Transfers & Bills", defaultVal: "false", desc: "When true, every transfer and bill payment is immediately rejected." }
    ]
  },
  nightCutoff: {
    title: "Night Cut-off (Global)",
    description: "Applies to outbound transfers + bills. Approved held transactions still execute.",
    keys: [
      { key: "night_cutoff.enabled", type: "BOOLEAN", label: "Enable Night Cut-off", defaultVal: "true", desc: "Master toggle for night cut-off window." },
      { key: "night_cutoff.start_time", type: "TIME", label: "Start Time (HH:mm)", defaultVal: "23:00", desc: "Cut-off window opens (Africa/Lagos time)." },
      { key: "night_cutoff.end_time", type: "TIME", label: "End Time (HH:mm)", defaultVal: "09:00", desc: "Cut-off window closes." }
    ]
  },
  velocityGlobal: {
    title: "Velocity Control (Global)",
    description: "Global toggle for transaction velocity limits.",
    keys: [
      { key: "velocity.enabled", type: "BOOLEAN", label: "Enable Velocity Limits", defaultVal: "false", desc: "Master toggle. When false, velocity guards are a no-op." }
    ]
  },
  velocityTiered: {
    title: "Velocity Limits (Tier-Aware)",
    description: "Per-user transaction limits. Supports override tiers (A, B, C). If a tier is not overridden, it inherits from the base key.",
    keys: [
      { key: "velocity.max_burst", type: "INTEGER", label: "Max Burst Count", defaultVal: "5", desc: "Max transactions allowed within the burst window." },
      { key: "velocity.burst_window_sec", type: "INTEGER", label: "Burst Window (Seconds)", defaultVal: "60", desc: "Timeframe for the burst count check." },
      { key: "velocity.max_daily_count", type: "INTEGER", label: "Max Daily Count", defaultVal: "20", desc: "Max transactions allowed per rolling 24 hours." },
      { key: "velocity.max_daily_amount", type: "DECIMAL", label: "Max Daily Amount (NGN)", defaultVal: "100000", desc: "Total NGN moved across transfers + bills in a Lagos calendar day." },
      { key: "velocity.large_tx_threshold", type: "DECIMAL", label: "Large Transaction Threshold (NGN)", defaultVal: "100000", desc: "Amount that triggers the large-transaction cooldown." },
      { key: "velocity.large_cooldown_sec", type: "INTEGER", label: "Large TX Cooldown (Seconds)", defaultVal: "300", desc: "Cooldown duration required after a large transaction." },
      { key: "velocity.min_interval_sec", type: "INTEGER", label: "Min Interval Between TXs (Seconds)", defaultVal: "300", desc: "Minimum seconds required between any two transactions." }
    ]
  },
  duplicateGuard: {
    title: "Duplicate Transfer Guard",
    description: "Prevents accidental double-tap transfers.",
    keys: [
      { key: "duplicate_transfer.enabled", type: "BOOLEAN", label: "Enable Duplicate Guard", defaultVal: "false", desc: "Master toggle." },
      { key: "duplicate_transfer.window_sec", type: "INTEGER", label: "Duplicate Window (Seconds)", defaultVal: "120", desc: "Within this window, the same user, amount, and destination is blocked." }
    ]
  },
  hmac: {
    title: "HMAC Request Signing",
    description: "Cryptographic request integrity verification.",
    keys: [
      { key: "hmac.enabled", type: "BOOLEAN", label: "Enable HMAC Filter", defaultVal: "false", desc: "Master toggle." },
      { key: "hmac.enforce", type: "BOOLEAN", label: "Enforce Signature Check", defaultVal: "false", desc: "false = log-only (compute but do not reject), true = reject invalid/missing signature with 401." },
      { key: "hmac.timestamp_window_sec", type: "INTEGER", label: "Replay Window (Seconds)", defaultVal: "60", desc: "Max age of a signed request before it's treated as a replay." },
      { key: "hmac.protected_paths", type: "LIST", label: "Protected Path Prefixes", defaultVal: "/api/v1/transfer,/api/v1/bills", desc: "Comma-separated URL path prefixes where signing is enforced." }
    ]
  },
  clientId: {
    title: "Client-ID Allow-list",
    description: "Enforces X-Client-Id header for authenticated customer traffic.",
    keys: [
      { key: "client_id.enabled", type: "BOOLEAN", label: "Enable Client-ID Check", defaultVal: "false", desc: "Master toggle." },
      { key: "client_id.enforce", type: "BOOLEAN", label: "Enforce Client-ID", defaultVal: "false", desc: "false = log-only, true = reject with 400." },
      { key: "client_id.allowed_ids", type: "LIST", label: "Allowed Client IDs", defaultVal: "", desc: "Comma-separated allowed client IDs. If empty when enabled, all customer requests fail." }
    ]
  },
  monitoring: {
    title: "Monitoring & Notifications",
    description: "Email notifications for transactions and reports.",
    keys: [
      { key: "monitoring.enabled", type: "BOOLEAN", label: "Enable Monitoring Emails", defaultVal: "false", desc: "Master toggle for both per-tx and hourly emails." },
      { key: "monitoring.per_tx_enabled", type: "BOOLEAN", label: "Enable Per-TX Emails", defaultVal: "true", desc: "Per-transaction firehose email." },
      { key: "monitoring.hourly_enabled", type: "BOOLEAN", label: "Enable Hourly Summary Emails", defaultVal: "true", desc: "Hourly Excel summary email." },
      { key: "monitoring.recipients", type: "LIST", label: "Recipients List", defaultVal: "", desc: "Comma-separated email addresses." }
    ]
  },
  routing: {
    title: "Inter-bank Routing",
    description: "Configure default routing channel.",
    keys: [
      { key: "inter_bank.route", type: "STRING", label: "Default Inter-bank Route", defaultVal: "CBA_NIBSS", desc: "CBA_NIBSS or NIP_OUTWARD. Selects the channel for inter-bank transfers." }
    ]
  }
};

const RuntimeConfigSettings = () => {
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [dbConfigs, setDbConfigs] = useState({});
  const [velocityTier, setVelocityTier] = useState("all"); // "all", "A", "B", "C"

  // Load all configurations
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/config`);
      const data = res?.data || res;
      if (data && Array.isArray(data)) {
        const configMap = {};
        data.forEach(item => {
          if (item && item.configKey) {
            configMap[item.configKey] = item;
          }
        });
        setDbConfigs(configMap);
      }
    } catch (err) {
      NotificationManager.error(err.message || "Failed to load runtime configurations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  // Validation function
  const validateValue = (val, type) => {
    if (val === undefined || val === null || val === '') {
      return { valid: false, msg: "Value cannot be empty" };
    }
    const strVal = String(val).trim();

    if (type === "BOOLEAN") {
      if (strVal !== "true" && strVal !== "false" && strVal !== "1" && strVal !== "0") {
        return { valid: false, msg: "Must be 'true' or 'false'" };
      }
    } else if (type === "INTEGER") {
      if (!/^\d+$/.test(strVal)) {
        return { valid: false, msg: "Must be a valid integer" };
      }
    } else if (type === "DECIMAL") {
      if (!/^\d+(\.\d+)?$/.test(strVal)) {
        return { valid: false, msg: "Must be a valid decimal number (no commas)" };
      }
    } else if (type === "TIME") {
      if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/.test(strVal)) {
        return { valid: false, msg: "Must be in HH:mm or HH:mm:ss format" };
      }
    }
    return { valid: true };
  };

  const handleSave = async (rawKey, type, currentFormValue, description) => {
    let resolvedKey = rawKey;
    if (velocityTier !== "all" && rawKey.startsWith("velocity.") && rawKey !== "velocity.enabled") {
      resolvedKey = `${rawKey}.${velocityTier}`;
    }

    // Determine value to submit
    let valueToSubmit = currentFormValue;
    if (type === "BOOLEAN" && typeof currentFormValue === "boolean") {
      valueToSubmit = currentFormValue ? "true" : "false";
    }

    const valCheck = validateValue(valueToSubmit, type);
    if (!valCheck.valid) {
      NotificationManager.error(`${resolvedKey}: ${valCheck.msg}`);
      return;
    }

    setSavingKey(resolvedKey);
    try {
      const payload = {
        configKey: resolvedKey,
        value: String(valueToSubmit).trim(),
        valueType: type,
        description: description || ""
      };

      const res = await httpPut(`${customBaseUrl.tranv2}/api/v1/admin/config`, payload);
      if (res && (res.success || res.status === "SUCCESS" || res.status === true)) {
        Swal.fire({
          title: "Configuration Saved",
          html: `Key <strong>${resolvedKey}</strong> updated successfully.<br/><br/><span style="color: #ff4d4f; font-weight: bold;">Note: Changes will propagate to all pods within 30 seconds (cache TTL).</span>`,
          icon: "success",
          confirmButtonColor: "#ff6700"
        });
        fetchConfigs();
      } else {
        NotificationManager.error(res?.message || "Failed to update configuration.");
      }
    } catch (err) {
      NotificationManager.error(err.message || "An error occurred while saving.");
    } finally {
      setSavingKey(null);
    }
  };

  const renderField = (item) => {
    let resolvedKey = item.key;
    let isOverridden = false;
    let inheritedValue = null;

    if (velocityTier !== "all" && item.key.startsWith("velocity.") && item.key !== "velocity.enabled") {
      resolvedKey = `${item.key}.${velocityTier}`;
      const baseConfig = dbConfigs[item.key];
      if (baseConfig) {
        inheritedValue = baseConfig.configValue;
      } else {
        inheritedValue = item.defaultVal;
      }

      if (dbConfigs[resolvedKey]) {
        isOverridden = true;
      }
    }

    const config = dbConfigs[resolvedKey];
    const currentValue = config ? config.configValue : (inheritedValue || item.defaultVal);

    return (
      <Card
        key={resolvedKey}
        size="small"
        style={{
          marginBottom: 12,
          borderRadius: 8,
          border: isOverridden ? '1px solid #b7eb8f' : '1px solid #f0f0f0',
          backgroundColor: isOverridden ? '#f6ffed' : '#ffffff'
        }}
      >
        <Row align="middle" justify="space-between" gutter={16}>
          <Col xs={24} sm={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong style={{ fontSize: '14px', color: '#262626' }}>{item.label}</strong>
              <Tooltip title={item.desc}>
                <InfoCircleOutlined style={{ color: '#bfbfbf', cursor: 'pointer' }} />
              </Tooltip>
              {velocityTier !== "all" && item.key.startsWith("velocity.") && item.key !== "velocity.enabled" && (
                <Badge
                  status={isOverridden ? "success" : "default"}
                  text={isOverridden ? `Override Active (Tier ${velocityTier})` : `Inherited (Tier ${velocityTier})`}
                />
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: 4 }}>
              Key: <code>{resolvedKey}</code> | Type: <code>{item.type}</code>
            </div>
            {inheritedValue !== null && !isOverridden && (
              <div style={{ fontSize: '11px', color: '#ff7875', marginTop: 2 }}>
                Inherits from base: <code>{inheritedValue}</code>
              </div>
            )}
            {config?.updatedBy && (
              <div style={{ fontSize: '10px', color: '#bfbfbf', marginTop: 2 }}>
                Last updated by: {config.updatedBy}
              </div>
            )}
          </Col>

          <Col xs={24} sm={12} style={{ textAlign: 'right', marginTop: { xs: 8, sm: 0 } }}>
            <Form
              key={`${resolvedKey}_${currentValue}`}
              layout="inline"
              initialValues={{ value: item.type === "BOOLEAN" ? currentValue === "true" : currentValue }}
              onFinish={(values) => handleSave(item.key, item.type, values.value, item.desc)}
              style={{ justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'nowrap' }}
            >
              <Form.Item name="value" valuePropName={item.type === "BOOLEAN" ? "checked" : "value"} style={{ marginRight: 8, marginBottom: 0 }}>
                {item.type === "BOOLEAN" ? (
                  <Switch checkedChildren="True" unCheckedChildren="False" />
                ) : item.type === "STRING" && item.key === "inter_bank.route" ? (
                  <Select style={{ width: 160 }}>
                    <Option value="CBA_NIBSS">CBA_NIBSS</Option>
                    <Option value="NIP_OUTWARD">NIP_OUTWARD</Option>
                  </Select>
                ) : (
                  <Input style={{ width: 180 }} placeholder={`Default: ${item.defaultVal}`} />
                )}
              </Form.Item>
              <Form.Item style={{ marginRight: 0, marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={savingKey === resolvedKey}
                  style={{ background: '#ff6700', borderColor: '#ff6700' }}
                >
                  Save
                </Button>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderGroup = (groupKey) => {
    const group = CATALOG[groupKey];
    return (
      <div key={groupKey} style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0d1a26', marginBottom: 4 }}>{group.title}</h3>
        <p style={{ fontSize: '13px', color: '#8c8c8c', marginBottom: 12 }}>{group.description}</p>
        {group.keys.map(renderField)}
        <Divider style={{ margin: '16px 0' }} />
      </div>
    );
  };

  return (
    <Spin spinning={loading} tip="Loading configurations...">
      <div style={{ padding: '8px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '12px 16px', background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8 }}>
        <span style={{ color: '#595959', fontSize: '13px' }}>
          Config changes propagate automatically to all application pods within 30 seconds.
        </span>
        <Button size="small" type="default" icon={<ReloadOutlined />} onClick={fetchConfigs}>
          Reload Configs
        </Button>
      </div>

        {/* Global toggles and switches */}
        {renderGroup("killSwitch")}
        {renderGroup("nightCutoff")}
        {renderGroup("velocityGlobal")}

        {/* Tier-aware Velocity */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#0d1a26', marginBottom: 4 }}>
                {CATALOG.velocityTiered.title}
              </h3>
              <p style={{ fontSize: '13px', color: '#8c8c8c', margin: 0 }}>
                {CATALOG.velocityTiered.description}
              </p>
            </div>
            <Radio.Group value={velocityTier} onChange={(e) => setVelocityTier(e.target.value)} buttonStyle="solid">
              <Radio.Button value="all">Base (All Tiers)</Radio.Button>
              <Radio.Button value="A">Tier A</Radio.Button>
              <Radio.Button value="B">Tier B</Radio.Button>
              <Radio.Button value="C">Tier C</Radio.Button>
            </Radio.Group>
          </div>
          {CATALOG.velocityTiered.keys.map(renderField)}
          <Divider style={{ margin: '16px 0' }} />
        </div>

        {/* Other groups */}
        {renderGroup("duplicateGuard")}
        {renderGroup("hmac")}
        {renderGroup("clientId")}
        {renderGroup("monitoring")}
        {renderGroup("routing")}
      </div>
    </Spin>
  );
};

export default RuntimeConfigSettings;
